import { InfoCircleIcon } from "../../../shared/Icons";
import { themeAtom } from "../../../shared/atoms";
import { toastError, toastSuccess } from "../../../shared/components/ToastContainer";
import WithTooltip from "../../../shared/components/WithTooltip";
import { log, logE, logV } from "../../../shared/services/debug-logger";
import {
  AdminCronJob,
  AdminCronParam,
  AdminCronParamValues,
  AdminCronRunResult,
  AdminCronVariant,
} from "../../models/admin-cron.models";
import { getAdminCronErrorMessage, runAdminCronJob } from "../../services/admin-cron.service";
import { adminStyles } from "../../styles/admin.styles";
import { buildInitialParamValues, hasRequiredParamValuesForVariant } from "../../utils/admin-cron.utils";
import CronEmailVerificationAction from "./CronEmailVerificationAction";
import CronJobRunResult, { CronJobRunDetails, CronJobRunPreview } from "./CronJobRunResult";
import OnboardingCertificateCandidatesTable from "./OnboardingCertificateCandidatesTable";
import { ExclamationTriangleIcon, PlayIcon } from "@heroicons/react/24/outline";
import { useAtomValue } from "jotai";
import { useMemo, useState } from "react";
import { twMerge } from "tailwind-merge";

interface Props {
  job: AdminCronJob;
}

const CronParamInput = ({
  param,
  value,
  onChange,
}: {
  param: AdminCronParam;
  value: string | boolean | undefined;
  onChange: (key: string, value: string | boolean) => void;
}) => {
  if (param.type === "checkbox") {
    return (
      <label className="flex items-start gap-2 text-sm">
        <input
          type="checkbox"
          checked={Boolean(value)}
          className="mt-1 h-4 w-4"
          onChange={(event) => onChange(param.key, event.target.checked)}
        />
        <span>
          <span className="font-medium">{param.label}</span>
          {param.helper && <span className="mt-0.5 block text-xs opacity-75">{param.helper}</span>}
        </span>
      </label>
    );
  }

  return (
    <label className="min-w-0 text-sm">
      <span className="mb-1 block font-medium">
        {param.label}
        {param.required && <span className="ml-1 text-[#BF0002]">*</span>}
      </span>
      <input
        type={param.type}
        value={typeof value === "string" ? value : ""}
        placeholder={param.placeholder}
        className="h-10 w-full border border-gray-300 bg-transparent px-3 text-sm outline-none focus:border-green"
        onChange={(event) => onChange(param.key, event.target.value)}
      />
      {param.helper && <span className="mt-1 block text-xs opacity-75">{param.helper}</span>}
    </label>
  );
};

const CronVariantPanel = ({
  title,
  tone,
  variants,
  isRunning,
  canRunVariant,
  onRun,
}: {
  title: string;
  tone: "safe" | "live";
  variants: AdminCronVariant[];
  isRunning: boolean;
  canRunVariant: (variant: AdminCronVariant) => boolean;
  onRun: (variant: AdminCronVariant) => void;
}) => {
  const isLive = tone === "live";

  return (
    <section
      className={twMerge(isLive ? adminStyles.panel.live : adminStyles.panel.safe)}
      data-test={`admin-cron-${tone}-panel`}
    >
      <p className={twMerge("mb-4 text-sm font-semibold uppercase", isLive ? "text-[#BF0002]" : "text-green")}>
        {title}
      </p>

      {variants.length > 0 ? (
        <div className="space-y-4">
          {variants.map((variant) => (
            <CronVariantAction
              key={variant.id}
              variant={variant}
              isRunning={isRunning}
              canRun={canRunVariant(variant)}
              onRun={onRun}
            />
          ))}
        </div>
      ) : (
        <div className="flex min-h-[120px] items-center justify-center bg-white/40 px-4 text-center text-sm opacity-70">
          No endpoint configured for this side.
        </div>
      )}
    </section>
  );
};

const CronVariantAction = ({
  variant,
  isRunning,
  canRun,
  onRun,
}: {
  variant: AdminCronVariant;
  isRunning: boolean;
  canRun: boolean;
  onRun: (variant: AdminCronVariant) => void;
}) => {
  const isLive = variant.id === "prod" || variant.risk === "prod";
  const isVerification = variant.risk === "verification";

  return (
    <div>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <code className="text-xs opacity-75">{variant.endpoint}</code>
        <WithTooltip id={`admin-cron-variant-info-${variant.endpoint}`} content={variant.description}>
          <InfoCircleIcon
            className={twMerge(
              "h-4 w-4 cursor-help",
              isLive ? "text-[#BF0002]" : isVerification ? "text-[#946200]" : "text-green",
            )}
          />
        </WithTooltip>
      </div>

      <button
        type="button"
        disabled={isRunning || !canRun}
        data-test={`admin-cron-run-${variant.endpoint}`}
        className={twMerge(
          adminStyles.button.primary,
          isLive ? adminStyles.button.live : adminStyles.button.safe,
          adminStyles.button.disabled,
        )}
        onClick={() => onRun(variant)}
      >
        {isLive ? <ExclamationTriangleIcon className="h-5 w-5" /> : <PlayIcon className="h-5 w-5" />}
        {isRunning ? "Running..." : variant.label}
      </button>
    </div>
  );
};

/**
 * Focused cron action page with side-by-side dry-run and live-run controls.
 */
const CronJobCard = ({ job }: Props) => {
  const theme = useAtomValue(themeAtom);
  const [paramValues, setParamValues] = useState<AdminCronParamValues>(() => buildInitialParamValues(job.params));
  const [result, setResult] = useState<AdminCronRunResult | null>(null);
  const [runningVariantId, setRunningVariantId] = useState<string | null>(null);

  const safeVariants = job.variants.filter((variant) => variant.id !== "prod" && variant.risk !== "prod");
  const liveVariants = job.variants.filter((variant) => variant.id === "prod" || variant.risk === "prod");
  const isRunning = runningVariantId !== null;
  const canRunVariant = useMemo(
    () => (variant: AdminCronVariant) => hasRequiredParamValuesForVariant(job.params, paramValues, variant),
    [job.params, paramValues],
  );

  const updateParam = (key: string, value: string | boolean) => {
    setParamValues((previous) => ({ ...previous, [key]: value }));
  };

  const selectOnboardingCandidate = (imoNumber: string) => {
    updateParam("imoNumber", imoNumber);
  };

  const runVariant = async (
    variant: AdminCronVariant,
    values: AdminCronParamValues = paramValues,
    parameters: AdminCronParam[] | undefined = job.params,
    confirmationMessage?: string,
  ) => {
    if (!hasRequiredParamValuesForVariant(parameters, values, variant)) {
      log("CronJobCard - runVariant - missing parameters", { job: job.id });
      toastError("Required cron parameters are missing.");
      return;
    }

    const confirmation =
      confirmationMessage ?? (variant.id === "prod" || variant.risk === "prod" ? `Run ${variant.endpoint}?` : null);
    if (confirmation && !window.confirm(confirmation)) {
      log("CronJobCard - runVariant - cancelled", { job: job.id });
      return;
    }

    log("CronJobCard - runVariant", { job: job.id, variant: variant.id });
    const startedAt = performance.now();
    setRunningVariantId(variant.id);

    try {
      const data = await runAdminCronJob(variant, values);
      log("CronJobCard - runVariant - completed", { durationMs: Math.round(performance.now() - startedAt) });
      setResult({
        status: "success",
        endpoint: variant.endpoint,
        variantLabel: variant.label,
        risk: variant.risk,
        durationMs: performance.now() - startedAt,
        finishedAt: new Date().toISOString(),
        data,
      });
      toastSuccess("Cron job completed.");
    } catch (error) {
      logE(new Error("CronJobCard - runVariant - failed"));
      const errorMessage = getAdminCronErrorMessage(error);
      setResult({
        status: "error",
        endpoint: variant.endpoint,
        variantLabel: variant.label,
        risk: variant.risk,
        durationMs: performance.now() - startedAt,
        finishedAt: new Date().toISOString(),
        errorMessage,
      });
      toastError(errorMessage);
    } finally {
      setRunningVariantId(null);
    }
  };

  logV("CronJobCard - BUILD", { job: job.id, isRunning });

  return (
    <article data-test={`admin-cron-job-${job.id}`} className="min-h-0 flex-1 overflow-y-auto">
      <div className="mb-6">
        <div className="mb-3 flex flex-wrap items-center gap-3">
          <h2 className={twMerge(adminStyles.heading.sectionTitle, theme.home.tailwind.textDefault)}>{job.title}</h2>
          <WithTooltip
            id={`admin-cron-output-focus-${job.id}`}
            content={job.outputFocus}
            tooltipClasses="z-[120] max-w-[320px]"
          >
            <InfoCircleIcon className="h-5 w-5 cursor-help text-green" />
          </WithTooltip>
        </div>
        <p className={twMerge(adminStyles.heading.body, theme.uwhcAssistant.textOutput)}>{job.purpose}</p>
      </div>

      {job.params && job.params.length > 0 && (
        <div
          className={twMerge(
            adminStyles.panel.params,
            "mb-5 md:grid-cols-2 xl:grid-cols-3",
            theme.home.tailwind.panelBackground,
          )}
          data-test={`admin-cron-params-${job.id}`}
        >
          {job.params.map((param) => (
            <CronParamInput key={param.key} param={param} value={paramValues[param.key]} onChange={updateParam} />
          ))}
        </div>
      )}

      {job.emailVerification && (
        <CronEmailVerificationAction
          action={job.emailVerification}
          isRunning={isRunning}
          isSending={runningVariantId === job.emailVerification.variant.id}
          onSend={runVariant}
        />
      )}

      <div className="mb-5">{result ? <CronJobRunResult result={result} /> : <CronJobRunPreview />}</div>

      {job.id === "create-onboarding-certificate-requests" &&
        result?.endpoint === "run-create-onboarding-certificate-requests-dev" && (
          <OnboardingCertificateCandidatesTable
            result={result}
            selectedImoNumber={typeof paramValues.imoNumber === "string" ? paramValues.imoNumber : ""}
            onSelectCandidate={selectOnboardingCandidate}
          />
        )}

      <div
        className={adminStyles.panel.actionGrid}
        data-test={`admin-cron-actions-${job.id}`}
        role="group"
        aria-label={`${job.title} actions`}
      >
        <CronVariantPanel
          title="Safe check"
          tone="safe"
          variants={safeVariants}
          isRunning={isRunning}
          canRunVariant={canRunVariant}
          onRun={runVariant}
        />
        <CronVariantPanel
          title="Live run"
          tone="live"
          variants={liveVariants}
          isRunning={isRunning}
          canRunVariant={canRunVariant}
          onRun={runVariant}
        />
      </div>

      {result && <CronJobRunDetails result={result} />}
    </article>
  );
};

export default CronJobCard;
